import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  LocateFixed,
  LockKeyhole,
  MapPinned,
  Sparkles,
  Users,
} from "lucide-react";
import { api, ApiError } from "../lib/api";

type Mode = "login" | "register";
type RegisterPath = "create" | "join";

export function AuthScreen({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<Mode>("login");
  const [registerPath, setRegisterPath] = useState<RegisterPath>("create");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setLoading(true);
    const data = new FormData(event.currentTarget);
    try {
      if (mode === "login") {
        await api.login({
          email: String(data.get("email") ?? ""),
          password: String(data.get("password") ?? ""),
        });
      } else {
        await api.register({
          name: String(data.get("name") ?? ""),
          email: String(data.get("email") ?? ""),
          password: String(data.get("password") ?? ""),
          ...(registerPath === "create"
            ? { spaceName: String(data.get("spaceName") ?? "") }
            : {
                inviteCode: String(data.get("inviteCode") ?? "")
                  .trim()
                  .toUpperCase(),
              }),
        });
      }
      onAuthenticated();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "เชื่อมต่อไม่สำเร็จ กรุณาลองใหม่",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-shell">
      <div className="auth-atmosphere" aria-hidden="true">
        <span className="candy-orbit candy-orbit-one" />
        <span className="candy-orbit candy-orbit-two" />
        <span className="candy-star">✦</span>
      </div>

      <section className="auth-story">
        <div className="brand-lockup brand-lockup-light">
          <span className="brand-mark">
            <MapPinned size={25} strokeWidth={2.5} />
          </span>
          <span>Candy tracker</span>
        </div>
        <div className="auth-promise">
          <span className="eyebrow">
            <Sparkles size={14} /> อยู่ใกล้กัน แม้อยู่ไกลกัน
          </span>
          <h1>
            ทุกคนที่ห่วงใย
            <br />
            <em>อยู่บนแผนที่เดียวกัน</em>
          </h1>
          <p>
            ดูตำแหน่งสด เช็กว่าใครเคยไปที่ไหน
            และรู้ทันทีเมื่อคนสำคัญถึงบ้านอย่างปลอดภัย
          </p>
        </div>
        <div className="auth-proof">
          <span><LocateFixed size={17} /> GPS คุณภาพสูง</span>
          <span><Users size={17} /> เฉพาะคนที่เชิญ</span>
          <span><LockKeyhole size={17} /> หยุดแชร์ได้ทุกเวลา</span>
        </div>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <div className="mobile-brand">
            <div className="brand-lockup">
              <span className="brand-mark">
                <MapPinned size={23} strokeWidth={2.5} />
              </span>
              <span>Candy tracker</span>
            </div>
          </div>

          <div className="auth-heading">
            <span className="tiny-candy">●</span>
            <h2>{mode === "login" ? "กลับมาแล้วนะ" : "เริ่มแผนที่ของเรา"}</h2>
            <p>
              {mode === "login"
                ? "เข้าสู่ระบบเพื่อดูว่าทุกคนอยู่ที่ไหน"
                : "สร้างกลุ่มใหม่หรือเข้าร่วมด้วยรหัสเชิญ"}
            </p>
          </div>

          <div className="auth-switch" role="tablist" aria-label="รูปแบบการเข้าสู่ระบบ">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className={mode === "login" ? "active" : ""}
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              เข้าสู่ระบบ
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className={mode === "register" ? "active" : ""}
              onClick={() => {
                setMode("register");
                setError("");
              }}
            >
              สมัครใหม่
            </button>
          </div>

          {mode === "register" && (
            <div className="register-path">
              <button
                type="button"
                className={registerPath === "create" ? "active" : ""}
                onClick={() => setRegisterPath("create")}
              >
                <span className="path-icon">🍬</span>
                <span><b>สร้างกลุ่ม</b><small>ชวนคนอื่นทีหลัง</small></span>
                {registerPath === "create" && <Check size={17} />}
              </button>
              <button
                type="button"
                className={registerPath === "join" ? "active" : ""}
                onClick={() => setRegisterPath("join")}
              >
                <span className="path-icon">💌</span>
                <span><b>เข้าร่วมกลุ่ม</b><small>มีรหัสเชิญแล้ว</small></span>
                {registerPath === "join" && <Check size={17} />}
              </button>
            </div>
          )}

          <form className="auth-form" onSubmit={submit}>
            {mode === "register" && (
              <label>
                <span>ชื่อที่ให้เพื่อนเห็น</span>
                <input name="name" autoComplete="name" placeholder="เช่น แคนดี้" required minLength={2} />
              </label>
            )}

            {mode === "register" && registerPath === "create" && (
              <label>
                <span>ชื่อกลุ่มของคุณ</span>
                <input name="spaceName" placeholder="เช่น บ้านของเรา" required minLength={2} />
              </label>
            )}

            {mode === "register" && registerPath === "join" && (
              <label>
                <span>รหัสเชิญ</span>
                <input
                  name="inviteCode"
                  className="invite-input"
                  placeholder="เช่น CANDY88"
                  required
                  minLength={4}
                  autoCapitalize="characters"
                />
              </label>
            )}

            <label>
              <span>อีเมล</span>
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@example.com"
                required
              />
            </label>

            <label>
              <span>รหัสผ่าน</span>
              <div className="password-field">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  placeholder={mode === "login" ? "รหัสผ่านของคุณ" : "อย่างน้อย 8 ตัวอักษร"}
                  required
                  minLength={mode === "register" ? 8 : 1}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </label>

            {error && <div className="form-error" role="alert">{error}</div>}

            <button className="primary-button auth-submit" disabled={loading}>
              <span>
                {loading
                  ? "กำลังเชื่อมต่อ…"
                  : mode === "login"
                    ? "เข้าสู่แผนที่"
                    : registerPath === "create"
                      ? "สร้างกลุ่มของฉัน"
                      : "เข้าร่วมกลุ่ม"}
              </span>
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>

          <p className="privacy-note">
            ตำแหน่งจะเห็นได้เฉพาะสมาชิกในกลุ่ม และคุณหยุดแชร์ได้ทุกเวลา
          </p>
        </div>
      </section>
    </main>
  );
}
